import { detectDataDocumentKind, parseDataDocument, serializeDataDocument } from './dataEnvelope'
import { richHtmlToPlainText, sanitizeRichContentHtml } from './richHtml'
import { escapeHtml } from './richElements'

/** Shared by editor, preview, display and the server save boundary. */
export function sanitizeCustomHtml(html: string): string {
  return sanitizeRichContentHtml(html, true)
}

export function isCustomHtmlDocument(value: string): boolean {
  return detectDataDocumentKind(value) === 'custom-html'
}

export function parseCustomHtmlDocument(value: string): string | null {
  if (!isCustomHtmlDocument(value)) return null
  const envelope = parseDataDocument(value)
  if (envelope?.kind !== 'custom-html' || envelope.schemaVersion !== 1) return null
  const data = envelope.data as { html?: unknown } | null
  return data && typeof data.html === 'string' ? sanitizeCustomHtml(data.html) : null
}

export function serializeCustomHtmlDocument(html: string): string {
  const safe = sanitizeCustomHtml(html)
  return serializeDataDocument(
    { schemaVersion: 1, kind: 'custom-html', data: { html: safe } },
    // The existing save pipeline decodes entities after serializing. Keep the
    // fallback digest free of HTML delimiters even on kind-unaware surfaces;
    // exact content remains in the encoded payload.
    richHtmlToPlainText(safe).replaceAll('<', '‹').replaceAll('>', '›').replaceAll('&', '＆').split('\n'),
  )
}

/** Only the Custom HTML envelope uses this policy; native documents retain
 * their existing save contract. Malformed custom documents fail closed. */
export function sanitizeCustomHtmlForSave(value: string): string {
  return isCustomHtmlDocument(value)
    ? serializeCustomHtmlDocument(parseCustomHtmlDocument(value) ?? '')
    : value
}

export const CUSTOM_HTML_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox'

export function customHtmlSrcDoc(html: string): string {
  const policy = "default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; img-src https: http:; object-src 'none'; frame-src 'none'; base-uri 'none'; form-action 'none'"
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="referrer" content="no-referrer"><style>html{overflow-wrap:break-word}body{margin:0;display:flow-root}img{max-width:100%;height:auto}</style></head><body>${sanitizeCustomHtml(html)}</body></html>`
}

export function renderCustomHtmlFrame(value: string): string {
  const source = customHtmlSrcDoc(parseCustomHtmlDocument(value) ?? '')
  return `<iframe title="Custom HTML" sandbox="${CUSTOM_HTML_SANDBOX}" referrerpolicy="no-referrer" style="width:100%;height:720px;border:0" srcdoc="${escapeHtml(source)}"></iframe>`
}
